import React, { useState, useEffect, useRef, useCallback } from 'react';
import { versDate } from '../services/horodatage';
import { stageAPI, documentsAPI } from '../services/api';
import { dateFr as dateDuJour } from '../services/dates';
import StageBarreProgression from './StageBarreProgression';
import PhotoEtudiant from './PhotoEtudiant';
import StageBlocRepliable from './StageBlocRepliable';
import StageBlocDecision from './StageBlocDecision';
import StageModaleExclusion, { LIBELLE_MOTIF } from './StageModaleExclusion';
import StageChoixTuteur from './StageChoixTuteur';
// Lot D.1 : la conservation des documents (bloc du panneau, mention « purgé »).
import { BlocConservation, MentionPurge } from './DocumentsConservation';
import StageModale from './StageModale';
import { StageMessageEnvoye } from './StageFenetreEnvoi';
import './StageFenetreEnvoi.css';
import { teinteAvatar, initiales } from '../services/etudiantsAffichage';
import StageFicheLiaisonContenu, { LigneEtatFiche } from './StageFicheLiaisonContenu';

/**
 * SUIVI DE STAGE — le panneau latéral du dossier (lot 3.1)
 * ========================================================
 * Spec     : doc/modules/suivi_de_stage.md §1 « Clic sur une ligne → panneau latéral »
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (`.detail-panel`)
 *
 * ── DEUX COLONNES, DEUX POIGNÉES ───────────────────────────────────────────
 * La maquette dessine un panneau à **deux colonnes redimensionnables** : le
 * dossier, et une colonne Historique qui a **sa propre poignée**. La spec ne
 * parlait que d'« une section Historique » — c'est la maquette qui fait foi
 * (règle 4), et elle a raison : on lit l'historique EN REGARDANT le dossier,
 * pas à la place.
 *
 * ── LES BORNES, ET LA CONTRADICTION DE LA SPEC (écart E3) ──────────────────
 * Le §1 dit « 560 par défaut, min 400 / max 800 ». Le test S66 dit
 * « min 420 / max 920 ». La spec se contredit elle-même ; l'arbitrage du
 * cadrage retient le §1, et S66 est corrigé.
 *
 * ── LA LARGEUR EST MÉMORISÉE PAR UTILISATEUR ───────────────────────────────
 * ⚠️ La clé porte l'identifiant du compte. Deux personnes sur le même poste ne
 * doivent pas se marcher dessus — c'est la règle déjà appliquée au séparateur du
 * module Étudiants. La spec écrivait `detailPanelWidth`, sans utilisateur.
 *
 * ⚠️ `localStorage` et non une table : `utilisateurs_preferences` que la spec
 * invoque **n'existe pas**, et aucun module du projet n'en a — mesuré le
 * 2026-09-13. La convention maison est le stockage local par utilisateur. Un
 * magasin de préférences côté serveur est un sujet TRANSVERSE, pas un sujet
 * Stage.
 */

const LARGEUR_DEFAUT = 560;
const LARGEUR_MIN = 400;
const LARGEUR_MAX = 800;

const HIST_DEFAUT = 220;
const HIST_MIN = 160;
const HIST_MAX = 380;

// 22/10 (maquette rév. 11) : la colonne « Fiche de liaison », entre le dossier et l'Historique.
const FICHE_DEFAUT = 380;
const FICHE_MIN = 300;
const FICHE_MAX = 560;
// Sous cette largeur d'écran, l'Historique se réduit à une bande tant que la fiche est ouverte (un clic le déplie).
const ECRAN_LARGE = 1440;

const cleMemo = (suffixe, user) => `stage.panneau.${suffixe}.${user?.id || 'anonyme'}`;

function lireMemo(cle, defaut, min, max) {
    try {
        const v = parseInt(localStorage.getItem(cle), 10);
        if (!Number.isFinite(v)) return defaut;
        return Math.min(max, Math.max(min, v));
    } catch (_) {
        // Fenêtre privée, stockage bloqué : on retombe sur le défaut plutôt que
        // de faire échouer l'ouverture du panneau.
        return defaut;
    }
}

function ecrireMemo(cle, valeur) {
    try { localStorage.setItem(cle, String(valeur)); } catch (_) { /* sans effet */ }
}

// ⚠️ `versDate` et non `new Date` : une heure sans fuseau venant de la base est
// de l'UTC (écart E78) — lue comme locale, elle s'affichait deux heures trop tôt.
const dateFr = (iso) => {
    const d = versDate(iso);
    return d ? d.toLocaleDateString('fr-FR') : '—';
};

function Champ({ label, children }) {
    return (
        <div className="detail-field-inline">
            <label>{label}</label>
            <span>{children ?? '—'}</span>
        </div>
    );
}

/**
 * Le maître de stage et SA civilité (v82, décision A du 2026-10-17). Seule la civilité se modifie ici (crayon) : les
 * dossiers déjà validés n'en ont pas, et elle sert aux formules de politesse des courriels (Planning oraux).
 * Jamais devinée par le prénom : « non précisée » tant que personne ne l'a choisie.
 */
const ABREGE_CIVILITE = { Madame: 'Mme', Monsieur: 'M.' };
function MaitreEtCivilite({ fiche, modifiable, enCours, onEnregistrer }) {
    const [edition, setEdition] = useState(null);
    const nom = [fiche.maitre_nom, fiche.maitre_prenom].filter(Boolean).join(' ');
    if (!nom) return null;
    const civ = fiche.maitre_civilite || '';
    return (
        <>
            {civ ? `${ABREGE_CIVILITE[civ]} ${nom}` : nom}
            {!civ && <span className="stg-civ-np"> · civilité non précisée</span>}
            {modifiable && edition === null && (
                <button type="button" className="stg-civ-crayon" title="Saisir la civilité du maître" aria-label="Saisir la civilité du maître"
                        disabled={enCours} onClick={() => setEdition(civ)}>
                    <i className="fa-solid fa-pen" />
                </button>
            )}
            {edition !== null && (
                <span className="stg-civ-edit">
                    <select className="input-sm" value={edition} autoFocus onChange={(e) => setEdition(e.target.value)} aria-label="Civilité du maître">
                        <option value="">Non précisée</option>
                        <option value="Madame">Madame</option>
                        <option value="Monsieur">Monsieur</option>
                    </select>
                    <button type="button" className="btn btn-green btn-sm" disabled={enCours || edition === civ}
                            onClick={() => onEnregistrer(edition, () => setEdition(null))}>
                        <i className="fa-solid fa-check" /> Enregistrer
                    </button>
                    <button type="button" className="btn btn-outline btn-sm" disabled={enCours} onClick={() => setEdition(null)}>Annuler</button>
                </span>
            )}
        </>
    );
}

/** Date ET heure : « le lien a été ouvert » sans l'heure ne dit pas grand-chose. */
const momentFr = (iso) => {
    const d = versDate(iso);
    if (!d) return null;
    return d.toLocaleDateString('fr-FR') + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Le titre d'une section, avec son icône.
 *
 * ⚠️ Chaque section de la maquette porte une icône — mesuré le 2026-09-16 :
 * 10 sur 10 chez elle, 0 sur 7 chez nous. Ce n'est pas de l'ornement : dans une
 * colonne de 560 px où dix blocs se suivent, c'est l'icône qui permet de
 * retrouver la bonne d'un coup d'œil, sans relire les titres.
 */
function TitreSection({ icone, children, droite, classe = '' }) {
    return (
        <div className={'detail-section-title' + (classe ? ' ' + classe : '')}>
            <span><i className={'fa-solid ' + icone} /> {children}</span>
            {droite}
        </div>
    );
}

/**
 * ⚠️ LE RÉSUMÉ DES ENVOIS, SUR LA LIGNE DU TITRE (demandé le 2026-09-25).
 *
 * Bertrand : *« les lignes où apparaît le nombre de messages rajoutent une ligne
 * à chaque fois ; mets-le à côté de l'étape, en plus petit »*. Dans une colonne
 * de 560 px où douze sections se suivent, un bandeau de résumé coûtait une ligne
 * pleine pour deux mots.
 *
 * ⚠️ Ce composant ne rend QUE le bouton. Le détail déroulé s'affiche **sous**
 * l'en-tête, dans la section — un bloc qui rendrait les deux se retrouverait
 * avec sa liste d'envois **à l'intérieur de la ligne de titre**.
 *
 * ⚠️ Et l'état est tenu par le PANNEAU, pas par ce bouton : c'est ce qui permet
 * de tout refermer en changeant de dossier (écart E52).
 */
function ResumePliable({ ouvert, onBasculer, resume, titre }) {
    return (
        <button type="button" className="stg-repli-compact"
                onClick={onBasculer} aria-expanded={ouvert}
                title={titre || (ouvert ? 'Replier' : 'Dérouler')}>
            <span className="stg-repli-resume">{resume}</span>
            <i className={'fa-solid ' + (ouvert ? 'fa-chevron-down' : 'fa-chevron-right')} />
        </button>
    );
}

/** Une section dont les données n'existent pas encore : elle NOMME son lot. */

const tailleMo = (o) => (o / 1024 / 1024).toFixed(1).replace('.', ',') + ' Mo';
const tailleLisible = (o) => (o < 1024 * 1024 ? Math.max(1, Math.round(o / 1024)) + ' Ko' : tailleMo(o));

/**
 * ⑥ LA CONVENTION — lot 3.4 (écarts E81 → E87). La maquette ne dessine PAS ce
 * geste : `.btn-upload` y est une classe CSS morte, et le panneau n'y montre que
 * « convention_….pdf [Voir] ». Conçu au cadrage du 2026-09-25 :
 *   · le MODE, parmi ceux actifs pour la formation ;
 *   · le PDF : Téléverser, puis Voir / Remplacer — jusqu'à l'envoi (E87).
 * ⚠️ L'ENVOI arrive avec le portail, au lot 3.4 bis (E86) : livré seul, il aurait
 * expédié un lien vers une page qui n'existe pas encore. La section le DIT.
 */

/**
 * ⑦ LES DOCUMENTS DE RETOUR — lot 3.4 bis (écarts E91 → E95). La maquette ne
 * dessine PAS cet écran (« Cinq écrans que la maquette ne dessine pas ») : conçu
 * au cadrage du 2026-09-25. Pièce par pièce : état, Voir, Valider, Rejeter + motif
 * OBLIGATOIRE (E92). Puis l'une des deux clôtures, et la raison si elle est
 * impossible (E69).
 */
function SectionDocuments({ dossier, departementId, modifiable, enCours, agir, onMessage }) {
    const s = dossier.sections?.documents;
    const [rejet, setRejet] = useState(null);          // { depotId, motif }
    const [erreurVoir, setErreurVoir] = useState(null);
    if (!s) return null;
    const lectureSeule = !modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.' : null;
    const statut = s.statut;
    const jalon = { lien_depot_envoye: ['en attente du dépôt', ''], documents_deposes: ['à vérifier', ' stg-jalon-alerte'],
                    documents_a_corriger: ['à corriger', ' stg-jalon-alerte'], documents_valides: ['validés', ' stg-jalon-ok'] }[statut];

    const voir = async (documentId) => {
        const fenetre = window.open('', '_blank');
        try {
            const r = await documentsAPI.lire(documentId, departementId);
            const url = URL.createObjectURL(r.data);
            if (fenetre) fenetre.location.href = url; else window.location.href = url;
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { if (fenetre) fenetre.close(); setErreurVoir("Le document n'a pas pu être ouvert."); }
    };
    const decider = (depotId, decision, motif) => agir(
        () => stageAPI.deciderPiece(dossier.id, departementId, depotId, decision, motif),
        () => setRejet(null));

    const avantDepot = ['en_attente', 'lien_etudiant_envoye', 'fiche_etudiant_saisie', 'fiche_maitre_envoyee',
                        'fiche_maitre_validee', 'fiche_a_corriger', 'fiche_validee'].includes(statut);

    return (
        <div className="detail-section">
            <TitreSection icone="fa-folder-open"
                droite={jalon ? <span className={'stg-jalon' + jalon[1]}>{jalon[0]}</span> : null}>
                ⑧ Documents de retour
            </TitreSection>
            <RappelMessage dossier={dossier} modifiable={modifiable} enCours={enCours} agir={agir}
                           onMessage={onMessage} departementId={departementId} types={['correction']} />

            {avantDepot ? (
                <div className="stg-section-avenir"><i className="fa-solid fa-hourglass-half" /> Le dépôt s'ouvre à l'envoi de la convention (section ⑦).</div>
            ) : (<>
                {statut === 'lien_depot_envoye' && (
                    <div className="stg-section-avenir">
                        <i className="fa-solid fa-hourglass-half" /> En attente du dépôt de l'étudiant
                        {s.jeton?.ouvert_le ? <> — lien ouvert le {momentFr(s.jeton.ouvert_le)}</> : <> — lien pas encore ouvert</>}.
                    </div>
                )}
                {statut === 'documents_a_corriger' && (
                    <div className="stg-section-avenir">
                        <i className="fa-solid fa-rotate-left" /> Pièces renvoyées à l'étudiant : en attente d'une nouvelle version.
                    </div>
                )}

                <div className="stg-doc-liste">
                    {s.pieces.map((p) => {
                        const c = p.courante;
                        /**
                         * ⚠️ LE REJET EN COURS SE VOIT (défaut trouvé par Bertrand le 2026-09-26).
                         * Après « Rejeter… », la carte restait VERTE et disait « validée » : on
                         * croyait le clic sans effet, et on recliquait « Valider ». Tant que le
                         * motif s'écrit, la carte est rouge et dit ce qu'on attend d'elle.
                         */
                        const rejetOuvert = rejet?.depotId === c?.id;
                        const etat = rejetOuvert ? ['rejet en cours — écrivez le motif', 'fa-pen-to-square', 'rouge']
                            : !c ? ['attendue', 'fa-circle', 'gris']
                            : !c.soumise ? ['déposée, pas encore envoyée', 'fa-pen', 'gris']
                            : c.statut === 'valide' ? ['validée', 'fa-circle-check', 'vert']
                            : c.statut === 'rejete' ? ['rejetée', 'fa-circle-xmark', 'rouge']
                            : ['à vérifier', 'fa-magnifying-glass', 'orange'];
                        const decidable = s.a_decider && c && c.soumise;
                        return (
                            <div key={p.attendu_id} className={'stg-doc stg-doc-' + etat[2]}>
                                <div className="stg-doc-tete">
                                    <i className={'fa-solid ' + etat[1]} />
                                    <strong>{p.libelle}</strong>{p.obligatoire && <span className="stg-doc-oblig"> *</span>}
                                    <span className="stg-doc-etat">{etat[0]}</span>
                                </div>
                                {c && (
                                    <div className="stg-doc-fichier">
                                        <span>{c.nom_fichier} · {tailleLisible(c.taille_octets)} · déposée le {momentFr(c.depose_le)}
                                            {p.versions > 1 ? ` · ${p.versions} versions` : ''}</span>
                                        {c.purge_le ? <MentionPurge purgeLe={c.purge_le} /> : (
                                            <button type="button" className="btn btn-outline btn-sm" onClick={() => voir(c.document_id)}>
                                                <i className="fa-solid fa-eye" /> Voir
                                            </button>
                                        )}
                                    </div>
                                )}
                                {c?.statut === 'rejete' && <div className="stg-doc-motif">Motif : « {c.motif_rejet} »{c.decide_par ? ` — ${c.decide_par}` : ''}</div>}
                                {c?.statut === 'valide' && c.decide_par && !rejetOuvert && <div className="stg-doc-par">Validée par {c.decide_par}</div>}
                                {/* 2026-09-26 : les deux cas INTERNES au dossier — même fichier pour deux
                                    pièces, même fichier que la convention envoyée (ce qu'on MESURE, pas « non signée »). */}
                                {c?.meme_que_convention && (
                                    <div className="stg-doc-identique">
                                        <i className="fa-solid fa-triangle-exclamation" /> <b>Même fichier que la convention</b> envoyée par le secrétariat.
                                    </div>
                                )}
                                {c?.meme_fichier_que?.length > 0 && (
                                    <div className="stg-doc-identique">
                                        <i className="fa-solid fa-triangle-exclamation" /> <b>Même fichier</b> que la pièce « {c.meme_fichier_que.join(' », « ')} » de ce dossier.
                                    </div>
                                )}
                                {/* ⚠️ E93 : l'octet pour octet seulement — une reformulation échappe. */}
                                {c?.identique_a?.length > 0 && (
                                    <div className="stg-doc-identique">
                                        <i className="fa-solid fa-triangle-exclamation" /> Fichier <b>rigoureusement identique</b> à un dépôt de{' '}
                                        {c.identique_a.map((x) => `${x.etudiant} (${dateFr(x.le)})`).join(', ')}.
                                    </div>
                                )}
                                {decidable && (
                                    rejet?.depotId === c.id ? (
                                        <div className="stg-doc-rejet">
                                            <label className="stg-conv-libelle" style={{ display: 'block', marginTop: 6 }}>Motif du rejet — obligatoire, il sera envoyé à l'étudiant</label>
                                            <textarea className="input-sm" rows={2} autoFocus placeholder="Ce que l'étudiant doit corriger…"
                                                      value={rejet.motif} onChange={(e) => setRejet({ ...rejet, motif: e.target.value })} />
                                            {!rejet.motif.trim() && <div className="stg-conv-raison">Le motif est obligatoire : il dit à l'étudiant quoi corriger.</div>}
                                            <div className="stg-doc-gestes">
                                                <button type="button" className="btn btn-outline btn-sm" onClick={() => setRejet(null)}>Annuler</button>
                                                <button type="button" className="btn btn-danger btn-sm" disabled={!rejet.motif.trim() || enCours}
                                                        onClick={() => decider(c.id, 'rejete', rejet.motif)}>
                                                    <i className="fa-solid fa-xmark" /> Rejeter
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="stg-doc-gestes">
                                            {/* Une pièce déjà validée ne se revalide pas : un second clic
                                                envoyait une seconde validation (vu dans les journaux, pièce 38). */}
                                            <button type="button" className={'btn btn-sm ' + (c.statut === 'valide' ? 'btn-green' : 'btn-outline')}
                                                    disabled={!!lectureSeule || enCours || c.statut === 'valide'} onClick={() => decider(c.id, 'valide')}>
                                                <i className="fa-solid fa-check" /> {c.statut === 'valide' ? 'Validée' : 'Valider'}
                                            </button>
                                            <button type="button" className={'btn btn-sm ' + (c.statut === 'rejete' ? 'btn-danger' : 'btn-outline')}
                                                    disabled={!!lectureSeule || enCours}
                                                    onClick={() => setRejet({ depotId: c.id, motif: c.motif_rejet || '' })}>
                                                <i className="fa-solid fa-xmark" /> Rejeter…
                                            </button>
                                        </div>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>

                {s.a_decider && (
                    <div className="stg-doc-cloture">
                        <button type="button" className="btn btn-green btn-sm" disabled={!s.peut_valider || !!lectureSeule || enCours}
                                onClick={() => agir(() => stageAPI.validerDocuments(dossier.id, departementId))}>
                            <i className="fa-solid fa-circle-check" /> Valider les documents
                        </button>
                        <button type="button" className="btn btn-outline btn-sm" disabled={!s.peut_renvoyer || !!lectureSeule || enCours}
                                onClick={() => agir(() => stageAPI.renvoyerPieces(dossier.id, departementId), (d) => onMessage && onMessage(d))}>
                            <i className="fa-solid fa-rotate-left" /> Renvoyer les pièces à corriger
                        </button>
                        {(lectureSeule || s.raison_valider) && (
                            <div className="stg-conv-raison"><i className="fa-solid fa-circle-info" /> {lectureSeule || s.raison_valider}</div>
                        )}
                    </div>
                )}
                {statut === 'documents_a_corriger' && (
                    <div className="stg-doc-cloture">
                        <button type="button" className="btn btn-outline btn-sm" disabled={!!lectureSeule || enCours}
                                onClick={() => agir(() => stageAPI.preparerRelanceCorrection(dossier.id, departementId), (d) => onMessage && onMessage(d))}>
                            <i className="fa-solid fa-envelope" /> Relancer l'étudiant
                        </button>
                    </div>
                )}
                {erreurVoir && <div className="stg-encart stg-encart-erreur" style={{ marginTop: 8 }}><i className="fa-solid fa-circle-exclamation" /><span>{erreurVoir}</span></div>}
            </>)}
        </div>
    );
}

function SectionConvention({ dossier, departementId, modifiable, enCours, agir, onMessage }) {
    const s = dossier.sections?.convention;
    const [erreurFichier, setErreurFichier] = useState(null);
    const [ouverture, setOuverture] = useState(false);
    // Le glisser-déposer (demandé par Bertrand à la recette du 2026-09-25) : la zone
    // se colore tant qu'un fichier la survole.
    const [survol, setSurvol] = useState(false);
    const refFichier = useRef(null);
    if (!s) return null;

    const doc = s.document;
    const modeChoisi = (s.modes || []).find((m) => m.cle === s.mode);
    // ⚠️ La RAISON est toujours visible quand un geste est impossible (E69).
    const raisonGeste = !modifiable
        ? 'Lecture seule : votre profil ne permet pas de modifier ce module.'
        : (!s.ouverte ? s.raison : null);

    /**
     * UN seul chemin pour les deux gestes — « parcourir » et « glisser-déposer ».
     * ⚠️ Deux chemins auraient fini par ne plus contrôler la même chose : c'est
     * toujours celui qu'on emprunte le moins qui laisse passer le fichier de trop.
     */
    const traiterFichier = (f) => {
        if (!f) return;
        setErreurFichier(null);
        // ⚠️ Contrôlé ICI, avant l'envoi, pour ne pas faire attendre la fin d'un
        // transfert raté — et REcontrôlé par le serveur, qui ne croit pas l'écran.
        if (f.size > s.taille_max_octets) {
            setErreurFichier(`Votre fichier fait ${tailleMo(f.size)}, la limite est ${tailleMo(s.taille_max_octets)} — `
                + 'scannez-le en noir et blanc ou réduisez sa résolution.');
            return;
        }
        agir(() => stageAPI.televerserConvention(dossier.id, departementId, f))
            .catch(() => { /* l'erreur est affichée par `agir` */ });
    };
    const choisirFichier = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';           // pouvoir rechoisir le même fichier après un refus
        traiterFichier(f);
    };

    /** Les gestionnaires du dépôt — posés sur la zone vide ET sur la ligne du document. */
    const peutDeposer = !raisonGeste && !enCours && !doc?.fige_le;
    const gestesDepot = {
        onDragOver: (e) => {
            // ⚠️ Sans `preventDefault`, le navigateur OUVRE le PDF à la place de la page.
            e.preventDefault();
            e.dataTransfer.dropEffect = peutDeposer ? 'copy' : 'none';
            if (peutDeposer && !survol) setSurvol(true);
        },
        onDragLeave: (e) => {
            // Quitter un ENFANT de la zone déclenche aussi `dragleave` : on ne s'éteint
            // que si le curseur sort vraiment de la zone.
            if (!e.currentTarget.contains(e.relatedTarget)) setSurvol(false);
        },
        onDrop: (e) => {
            e.preventDefault();
            setSurvol(false);
            if (!peutDeposer) return;
            const fichiers = e.dataTransfer.files;
            if (!fichiers || !fichiers.length) return;
            if (fichiers.length > 1) {
                setErreurFichier('Déposez UN seul fichier : la convention.');
                return;
            }
            traiterFichier(fichiers[0]);
        }
    };

    const voir = async () => {
        // ⚠️ La fenêtre s'ouvre AVANT la requête : ouverte après un `await`, elle
        // serait prise pour une fenêtre surgissante et bloquée par le navigateur.
        const fenetre = window.open('', '_blank');
        setOuverture(true);
        try {
            const r = await documentsAPI.lire(doc.id, departementId);
            const url = URL.createObjectURL(r.data);
            if (fenetre) fenetre.location.href = url; else window.location.href = url;
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            if (fenetre) fenetre.close();
            setErreurFichier("Le document n'a pas pu être ouvert.");
        } finally {
            setOuverture(false);
        }
    };

    return (
        <div className="detail-section">
            <TitreSection icone="fa-file-contract"
                droite={doc
                    ? <span className={'stg-jalon' + (doc.fige_le ? ' stg-jalon-ok' : '')}>{doc.fige_le ? 'envoyée' : 'téléversée'}</span>
                    : null}>
                ⑦ Convention
            </TitreSection>

            {!s.ouverte && !doc && (
                <div className="stg-section-avenir"><i className="fa-solid fa-hourglass-half" /> {s.raison}</div>
            )}

            {(s.ouverte || doc) && (
                <>
                    <div className="stg-conv-libelle">Mode de convention</div>
                    <div className="stg-conv-modes" role="radiogroup" aria-label="Mode de convention">
                        {(s.modes || []).map((m) => (
                            <button key={m.cle} type="button" role="radio"
                                    aria-checked={s.mode === m.cle}
                                    className={'stg-conv-mode' + (s.mode === m.cle ? ' actif' : '')}
                                    disabled={!!raisonGeste || enCours}
                                    onClick={() => agir(() => stageAPI.choisirModeConvention(dossier.id, departementId, m.cle))}>
                                <i className={'fa-solid ' + ({ retrait: 'fa-building-columns', postal: 'fa-envelopes-bulk', numerique: 'fa-envelope-open-text' }[m.cle] || 'fa-file')} /> {m.libelle}
                            </button>
                        ))}
                    </div>
                    {modeChoisi
                        ? <div className="stg-conv-texte">« {modeChoisi.texte} »</div>
                        : <div className="stg-conv-texte stg-conv-manque">Aucun mode choisi.</div>}

                    <div className="stg-conv-libelle" style={{ marginTop: 10 }}>Document</div>
                    {doc ? (
                        <div className={'stg-conv-doc' + (survol ? ' survol' : '')} {...gestesDepot}
                             title={doc.fige_le ? undefined : 'Glissez un autre PDF ici pour remplacer la convention'}>
                            <i className="fa-solid fa-file-pdf" />
                            <div className="stg-conv-doc-texte">
                                <strong>{doc.nom_fichier}</strong>
                                <span>{tailleLisible(doc.taille_octets)} · téléversée le {momentFr(doc.depose_le)}
                                    {doc.depose_par ? ` par ${doc.depose_par}` : ''}</span>
                            </div>
                            {doc.purge_le ? <MentionPurge purgeLe={doc.purge_le} /> : (
                                <button type="button" className="btn btn-outline btn-sm" onClick={voir} disabled={ouverture}>
                                    <i className="fa-solid fa-eye" /> Voir
                                </button>
                            )}
                            {!doc.fige_le && (
                                <button type="button" className="btn btn-outline btn-sm"
                                        disabled={!!raisonGeste || enCours}
                                        onClick={() => refFichier.current && refFichier.current.click()}>
                                    <i className="fa-solid fa-rotate" /> Remplacer
                                </button>
                            )}
                        </div>
                    ) : (
                        <button type="button" className={'stg-conv-depot' + (survol ? ' survol' : '')}
                                disabled={!!raisonGeste || enCours}
                                onClick={() => refFichier.current && refFichier.current.click()}
                                {...gestesDepot}>
                            <i className={'fa-solid ' + (survol ? 'fa-file-arrow-down' : 'fa-file-arrow-up')} />
                            <span>{survol
                                ? <strong>Relâchez pour téléverser</strong>
                                : <><strong>Glissez la convention ici</strong> ou cliquez pour parcourir — PDF, {tailleMo(s.taille_max_octets)} maximum</>}</span>
                        </button>
                    )}
                    <input ref={refFichier} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
                           onChange={choisirFichier} />

                    {raisonGeste && (s.ouverte || !modifiable) && (
                        <div className="stg-conv-raison"><i className="fa-solid fa-lock" /> {raisonGeste}</div>
                    )}
                    {erreurFichier && (
                        <div className="stg-encart stg-encart-erreur" style={{ marginTop: 8 }}>
                            <i className="fa-solid fa-circle-exclamation" /><span>{erreurFichier}</span>
                        </div>
                    )}
                    {/* L'ENVOI (lot 3.4 bis) : la convention n'est PAS jointe — l'étudiant la
                        télécharge sur le portail (E81). À l'envoi, elle est figée (E87). */}
                    {(dossier.statut === 'fiche_validee' || dossier.statut === 'lien_depot_envoye') && (() => {
                        const manque = !s.mode ? 'Choisissez un mode de convention.'
                            : !doc ? 'Téléversez la convention.' : null;
                        const relance = dossier.statut === 'lien_depot_envoye';
                        return (
                            <div style={{ marginTop: 10 }}>
                                <button type="button" className="btn btn-green btn-sm"
                                        disabled={!modifiable || !!manque || enCours}
                                        onClick={() => agir(() => stageAPI.preparerEnvoiConvention(dossier.id, departementId),
                                                            (d) => onMessage && onMessage(d))}>
                                    <i className="fa-solid fa-paper-plane" /> {relance ? 'Relancer le dépôt' : 'Envoyer convention + lien dépôt'}
                                </button>
                                {(manque || !modifiable) && (
                                    <div className="stg-conv-raison"><i className="fa-solid fa-circle-info" /> {!modifiable ? raisonGeste : manque}</div>
                                )}
                                {!relance && !manque && (
                                    <div className="stg-conv-raison">La convention ne voyage pas en pièce jointe : l'étudiant la télécharge sur son espace, après identification. À l'envoi, elle est figée.</div>
                                )}
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
}

/** « 28/02/2027 » depuis `YYYY-MM-DD`, sans fuseau (E78). */

/** Une réponse du maître : critères, niveaux, points, commentaires, total, signataire. */
function DetailGrille({ r }) {
    const masquer = r.grille?.masquer_points === true;
    return (
        <div className="stg-eval-detail">
            {(r.notes || []).map((n) => (
                <div className="stg-eval-critere" key={n.critere_id}>
                    <div className="stg-eval-ligne">
                        <span>{n.ordre} — {n.libelle}</span>
                        <strong>{n.intitule}{!masquer && n.points !== null ? ` · ${n.points} pt${n.points > 1 ? 's' : ''}` : ''}</strong>
                    </div>
                    {n.commentaire && <div className="hist-commentaire">« {n.commentaire} »</div>}
                </div>
            ))}
            {!masquer && (
                <div className="stg-eval-total">
                    <span><i className="fa-solid fa-calculator" /> Total</span>
                    <span>{r.total} / {r.total_max} pts</span>
                </div>
            )}
            {r.commentaire && (
                <div style={{ marginTop: 6 }}>
                    <div className="stg-conv-libelle">Commentaire général</div>
                    <div className="hist-commentaire" style={{ whiteSpace: 'pre-wrap' }}>« {r.commentaire} »</div>
                </div>
            )}
            <div className="detail-fields-2col" style={{ marginTop: 6 }}>
                <Champ label="Signée par">{r.signature_nom}</Champ>
                <Champ label="Reçue le">{momentFr(r.date_soumission)}</Champ>
            </div>
        </div>
    );
}

/**
 * ⑨ L'ÉVALUATION DU MAÎTRE DE STAGE — lot 3.6 (écarts E99 → E102).
 * La maquette ne dessine que deux états (« à venir », « grille envoyée ») : les
 * autres suivent son vocabulaire — champs en deux colonnes, jalon à droite du
 * titre, raison visible sous tout geste impossible (E69).
 */
function SectionEvaluation({ dossier, modifiable, enCours, onEnvoyer, onRenvoyer, envois, envoisOuverts, basculer }) {
    const s = dossier.sections?.evaluation;
    if (!s) return null;
    const lectureSeule = !modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.' : null;
    const recue = dossier.statut === 'valide' && s.resultat;
    const jalon = recue ? ['reçue', ' stg-jalon-ok']
        : s.en_attente ? ['envoyée', '']
        : s.alerte ? ['à envoyer', ' stg-jalon-alerte'] : null;
    const g = s.grille;

    return (
        <div className="detail-section">
            <TitreSection icone="fa-star-half-stroke" classe="stg-titre-repliable"
                droite={<>
                    {envois.length > 0 && (
                        <ResumePliable ouvert={!!envoisOuverts} onBasculer={basculer}
                            resume={<ResumeEnvois envois={envois} destinataire="au maître de stage" />} titre="Voir le détail des messages" />
                    )}
                    {jalon && <span className={'stg-jalon' + jalon[1]}>{jalon[0]}</span>}
                </>}>
                {/* Le libellé de la maquette, au mot près — et le plus court : dans une
                    colonne de 340 px, « du » faisait sortir le jalon du panneau (vu à l'image). */}
                ⑩ Évaluation maître de stage
            </TitreSection>
            {envoisOuverts && <ListeEnvois envois={envois} />}

            {!s.active ? (
                <div className="stg-section-avenir">
                    <i className="fa-solid fa-circle-minus" /> Sans objet : pas d'évaluation par le maître pour cette formation.
                </div>
            ) : (<>
                {/* E101 : QUELLE grille, et si elle est déjà figée pour la campagne. */}
                {g ? (
                    <div className="stg-eval-grille">
                        <i className="fa-solid fa-table-list" /> <b>{g.nom}</b> · {g.nb_criteres} critère{g.nb_criteres > 1 ? 's' : ''}
                        {!g.masquer_points && <> · /{g.total_max} pts</>}
                        <div className="stg-conv-raison" style={{ marginTop: 2 }}>
                            {g.figee_le
                                ? <><i className="fa-solid fa-lock" /> Figée le {dateFr(g.figee_le)} pour toute la campagne : tous les maîtres notent sur cette grille.</>
                                : 'Elle sera figée pour toute la campagne au premier envoi.'}
                        </div>
                    </div>
                ) : (
                    <div className="stg-conv-raison"><i className="fa-solid fa-triangle-exclamation" /> Aucune grille choisie : Paramétrage → Processus → étape ⑩.</div>
                )}

                {recue && <DetailGrille r={s.resultat} />}

                {!recue && !s.en_attente && !s.envoyable && (
                    <div className="stg-section-avenir">
                        <i className="fa-solid fa-hourglass-half" /> Grille à envoyer à partir du {(dateDuJour(s.a_partir_du) || '—')}
                        {s.fin_stage ? ` (fin du stage le ${(dateDuJour(s.fin_stage) || '—')})` : ''}.
                        {s.raison_envoi && <> {s.raison_envoi}</>}
                    </div>
                )}

                {s.envoyable && (
                    <div style={{ marginTop: 8 }}>
                        {s.alerte
                            ? <div className="stg-doc-identique" style={{ marginBottom: 8 }}>
                                <i className="fa-solid fa-star" /> Grille à envoyer — fin du stage le {(dateDuJour(s.alerte.fin) || '—')}.
                              </div>
                            : <div className="stg-conv-raison" style={{ marginBottom: 6 }}>
                                Rappel prévu à partir du {(dateDuJour(s.a_partir_du) || '—')} — l'envoi est possible dès maintenant.
                              </div>}
                        <button type="button" className="btn btn-green btn-sm" disabled={!!lectureSeule || enCours || !g}
                                onClick={() => onEnvoyer(false)}>
                            <i className="fa-solid fa-star" /> Envoyer la grille d'évaluation
                        </button>
                        {lectureSeule && <div className="stg-conv-raison"><i className="fa-solid fa-circle-info" /> {lectureSeule}</div>}
                    </div>
                )}

                {s.en_attente && (
                    <>
                        <div className="detail-fields-2col" style={{ marginTop: 6 }}>
                            <Champ label="Grille envoyée le">{momentFr(s.envoyee_le)}</Champ>
                            <Champ label="Lien ouvert le">
                                {s.jeton ? (momentFr(s.jeton.ouvert_le) || <span style={{ color: '#b45309' }}>jamais ouvert</span>) : null}
                            </Champ>
                        </div>
                        <div className="stg-section-avenir" style={{ marginTop: 6 }}>
                            <i className="fa-solid fa-triangle-exclamation" /> En attente de la réponse du maître de stage.
                        </div>
                    </>
                )}

                {(s.en_attente || s.peut_renvoyer) && (
                    <div className="stg-doc-cloture">
                        {s.en_attente && (
                            <button type="button" className="btn btn-outline btn-sm" disabled={!!lectureSeule || enCours}
                                    onClick={() => onEnvoyer(true)}>
                                <i className="fa-solid fa-rotate-right" /> Relancer la grille
                            </button>
                        )}
                        {s.peut_renvoyer && (
                            <button type="button" className="btn btn-outline btn-sm" disabled={!!lectureSeule || enCours}
                                    onClick={onRenvoyer}
                                    title="Un nouveau lien remplace l'ancien ; la grille déjà reçue reste dans le dossier">
                                <i className="fa-solid fa-rotate" /> Renvoyer la grille (nouveau lien)
                            </button>
                        )}
                        {lectureSeule && <div className="stg-conv-raison"><i className="fa-solid fa-circle-info" /> {lectureSeule}</div>}
                    </div>
                )}

                {/* E100 : la grille précédente est GARDÉE, jamais effacée. */}
                {(s.precedentes || []).length > 0 && (
                    <div className="stg-conv-raison" style={{ marginTop: 8 }}>
                        <i className="fa-solid fa-box-archive" /> {s.precedentes.length} grille{s.precedentes.length > 1 ? 's' : ''} précédente{s.precedentes.length > 1 ? 's' : ''} gardée{s.precedentes.length > 1 ? 's' : ''} :
                        {' '}{s.precedentes.map((p) => `reçue le ${dateFr(p.date_soumission)} (${p.total}/${p.total_max}, signée ${p.signature_nom}), remplacée le ${dateFr(p.remplacee_le)}`).join(' · ')}
                    </div>
                )}
            </>)}
        </div>
    );
}

function SectionAVenir({ titre, icone, lot, texte }) {
    return (
        <div className="detail-section">
            <TitreSection icone={icone}>{titre}</TitreSection>
            <div className="stg-section-avenir">
                <i className="fa-solid fa-screwdriver-wrench" /> {texte}
                {' '}Livré au lot <b>{lot}</b>.
            </div>
        </div>
    );
}

/**
 * Lot 3.S-b2 (SM10) — « Message non envoyé » : la décision est prise, son message n'est pas
 * parti. ⚠️ Affiché EN HAUT (sous la progression) ET dans la section concernée : après une
 * décision le panneau reste positionné sur cette section, le rappel du haut serait hors de vue.
 */
function RappelMessage({ dossier, modifiable, enCours, agir, onMessage, departementId, types = null }) {
    const m = dossier.message_en_attente;
    if (!m || (types && !types.includes(m.type))) return null;
    return (
        <div className="stg-encart stg-encart-alerte" style={{ marginTop: 8, marginBottom: 8 }}>
            <i className="fa-solid fa-envelope-circle-check" />
            <span style={{ flex: 1 }}><b>Message non envoyé</b> : {m.libelle}.</span>
            {modifiable && (
                <button type="button" className="btn btn-green btn-sm" disabled={enCours}
                        onClick={() => agir(() => stageAPI.messageEnAttente(dossier.id, departementId),
                            (d) => onMessage && onMessage(d))}>
                    <i className="fa-solid fa-paper-plane" /> Envoyer maintenant
                </button>
            )}
        </div>
    );
}

/**
 * Lot 3.S-b2 : le département du panneau, pour que le journal des envois ouvre lui-même
 * « Voir le message envoyé » — sans faire descendre un paramètre dans chaque section.
 */
const DeptEnvois = React.createContext(null);

/** Les libellés des types de courriel, pour le journal des envois. */
const TYPE_ENVOI = {
    fiche_validee: 'Confirmation de validation',
    depot_a_corriger: 'Pièces à corriger',
    etudiant_lien: 'Lien à l\u2019étudiant',
    maitre_fiche: 'Fiche au maître de stage',
    convention_depot: 'Convention et lien de dépôt',
    tuteur_affectation: 'Information tuteur',
    maitre_suivi:       'Information du suivi (maître)',
    stage_refuse: 'Stage refusé',
    // E173 (v85)
    fiche_etudiant_a_corriger: 'Fiche à compléter (à l’étudiant)',
    fiche_modifiee_maitre: 'Alerte aux responsables (fiche modifiée par le maître)',
    grille_eval: 'Grille d\u2019évaluation',
    relance: 'Relance'
};

/**
 * Le résumé d'une liste d'envois — ce qu'on doit pouvoir lire SANS dérouler.
 *
 * ⚠️ Deux nombres, pas un. « 6 messages » mélangerait les relances nominatives
 * et les envois de masse qui touchaient toute la promotion. Or la question qu'on
 * se pose devant un dossier est « combien de fois l'a-t-on relancé, LUI ». La
 * répartition y répond sans ouvrir.
 */
function ResumeEnvois({ envois, destinataire = 'à cet étudiant' }) {
    const nominatifs = envois.filter((e) => e.portee !== 'promotion').length;
    const groupes = envois.length - nominatifs;
    const dernier = momentFr(envois[0] && envois[0].prepare_le);
    const parties = [];
    // ⚠️ E61 : le résumé NOMME le vrai destinataire — un message au maître n'est
    // pas « à cet étudiant » (vu à l'image au lot 3.6, en ③ comme en ⑨).
    if (nominatifs) parties.push(nominatifs + ' ' + destinataire);
    if (groupes) parties.push(groupes + ' à la promotion');
    // Lot 3.S-b2 (SM2) : « envoyé » = accepté par le serveur ; « préparé » = mailto.
    const envoyes = envois.filter((e) => e.mode === 'smtp' && e.resultat === 'envoye').length;
    const echecs = envois.filter((e) => e.resultat === 'echec').length;
    const prepares = envois.length - envoyes - echecs;
    const etat = [envoyes && `${envoyes} envoyé${envoyes > 1 ? 's' : ''}`, prepares && `${prepares} préparé${prepares > 1 ? 's' : ''}`,
                  echecs && `${echecs} en échec`].filter(Boolean).join(', ');
    return (
        <>
            <b>{envois.length} message{envois.length > 1 ? 's' : ''}{envoyes || echecs ? <> ({etat})</> : <> préparé{envois.length > 1 ? 's' : ''}</>}</b>
            {parties.length ? <> — {parties.join(', ')}</> : null}
            {dernier ? <span className="stg-repli-date"> · dernier le {dernier}</span> : null}
        </>
    );
}

/**
 * ⑤ LE JOURNAL DES ENVOIS.
 *
 * ⚠️ Il dit « préparé », jamais « envoyé » (écart E8) : en `mailto:`,
 * l'application perd la main dès que la fenêtre du client mail s'ouvre.
 * La seule preuve qu'un message est arrivé, c'est « lien ouvert » — qui vient
 * du jeton, pas de nous.
 */
function ListeEnvois({ envois }) {
    const departementId = React.useContext(DeptEnvois);
    const [vu, setVu] = useState(null);
    if (!envois || !envois.length) {
        return <div className="stg-section-avenir">
            <i className="fa-solid fa-inbox" /> Aucun message préparé pour ce dossier.
        </div>;
    }
    return (
        <div className="stg-envois">
            {envois.map((e, i) => (
                <div className={'stg-envoi' + (e.portee === 'promotion' ? ' stg-envoi-groupe' : '')} key={i}>
                    <div className="stg-envoi-tete">
                        <b>{TYPE_ENVOI[e.type_mail] || e.type_mail}</b>
                        {/* Lot 3.S-b2 (SM2) : ce que le SERVEUR sait — envoyé (accepté) ou en échec. */}
                        {e.mode === 'smtp' && e.resultat === 'envoye' && <span className="stg-envoi-etat ok">envoyé</span>}
                        {e.resultat === 'echec' && <span className="stg-envoi-etat ko">échec</span>}
                        {(!e.mode || e.mode === 'mailto') && e.portee !== 'promotion' && <span className="stg-envoi-etat prep">préparé</span>}
                        <span className="stg-envoi-date">{momentFr(e.envoye_le || e.prepare_le) || '—'}</span>
                        {e.mode === 'smtp' && e.id && departementId && (
                            <button type="button" className="stg-envoi-voir" onClick={() => setVu(e.id)}>Voir le message</button>
                        )}
                    </div>
                    {e.resultat === 'echec' && e.erreur && <div className="stg-envoi-erreur">Refusé par le serveur : « {e.erreur} »</div>}
                    <div className="stg-envoi-sous">
                        {e.portee === 'promotion'
                            ? <>
                                {/* ⚠️ « probablement » n'est pas une coquetterie : un envoi
                                    groupé garde le NOMBRE de destinataires, pas leur liste.
                                    Écrire « reçu » serait affirmer ce qu'on ne sait pas. */}
                                <i className="fa-solid fa-users" /> envoi groupé à la promotion —
                                {' '}<b>{e.nb_destinataires}</b> destinataire{e.nb_destinataires > 1 ? 's' : ''},
                                {' '}cet étudiant en faisait probablement partie
                              </>
                            : <>à {e.destinataire || '—'}</>}
                        {e.prepare_par_nom ? <> · {e.mode === 'smtp' ? 'envoyé' : 'préparé'} par {e.prepare_par_nom}</> : null}
                        {e.redirige_vers ? <> · <i className="fa-solid fa-flask" /> parti à {e.redirige_vers} (serveur de test)</> : null}
                    </div>
                </div>
            ))}
            {vu && <StageMessageEnvoye envoiId={vu} departementId={departementId} onFermer={() => setVu(null)} />}
        </div>
    );
}

/**
 * ⑧ — le tuteur pédagogique (lot 3.5a). Maquette : section « ⑧ Tuteur / Visite » du panneau BUT2.
 * Le serveur dit tout : qui, depuis quand, l'échéance, le retard, et POURQUOI on ne peut plus changer.
 * Lot 3.5c-3 : le résumé de la fiche de visite, et « Voir le détail » qui ouvre le volet de l'onglet Évaluation.
 */
function SectionTuteur({ dossier, departementId, anneeId, modifiable, enCours, agir, onMessage, onVoirEvaluation }) {
    const s = dossier.sections?.visite;
    const [enseignants, setEnseignants] = useState(null);
    const [erreurListe, setErreurListe] = useState(null);
    const lire = useCallback(() => {
        stageAPI.getTuteurs(departementId, anneeId)
            .then((r) => { setEnseignants(r.data.enseignants || []); setErreurListe(null); })
            .catch((e) => setErreurListe(e.response?.data?.error || 'Lecture des enseignants impossible'));
    }, [departementId, anneeId]);
    // Relue quand le dossier change de tuteur : la charge de chacun a bougé.
    const aLire = !!s && !s.raison_verrou;
    const tuteurActuel = dossier.tuteur?.id || null;
    useEffect(() => { if (aLire) lire(); }, [lire, aLire, tuteurActuel]);
    if (!s) return null;
    const t = s.tuteur;
    const jalon = !t ? ['à affecter', ' stg-jalon-alerte'] : s.visite_faite ? ['visite faite', ' stg-jalon-ok']
        : s.en_retard ? ['en retard', ' stg-jalon-alerte'] : ['affecté', ' stg-jalon-ok'];
    return (
        <div className="detail-section">
            <TitreSection icone="fa-chalkboard-user" droite={<span className={'stg-jalon' + jalon[1]}>{jalon[0]}</span>}>⑨ Tuteur / Visite</TitreSection>
            {t && (
                <div className="stg-encart stg-encart-ok" style={{ marginBottom: 8 }}>
                    <i className="fa-solid fa-chalkboard-user" />
                    <span>
                        <b>{t.nom} {t.prenom || ''}</b>{s.affecte_le && <> — affecté le {dateFr(s.affecte_le)}</>}
                        {s.echeance_visite && <><br /><span style={{ color: s.en_retard ? '#b45309' : undefined }}>
                            {s.en_retard && <i className="fa-solid fa-clock" />} Visite attendue avant le {dateFr(s.echeance_visite)}
                        </span></>}
                    </span>
                </div>
            )}
            {s.raison_verrou ? (
                <div className="stg-conv-raison"><i className="fa-solid fa-lock" /> {s.raison_verrou}</div>
            ) : erreurListe ? (
                <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreurListe}</span></div>
            ) : !enseignants ? (
                <div className="stg-section-avenir"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture des enseignants…</div>
            ) : (
                <StageChoixTuteur enseignants={enseignants} actuelId={t?.id || null} enCours={enCours}
                    lectureSeule={modifiable ? null : 'Lecture seule : votre profil ne permet pas de modifier ce module.'}
                    onAffecter={(id) => agir(() => stageAPI.affecterTuteur(dossier.id, departementId, id), () => lire())} />
            )}
            {/* Lot 3.5a-3 : prévenir le tuteur (son lien de fiche de visite) et le maître (qui est le tuteur). */}
            {t && !s.raison_verrou && (
                <div className="stg-doc-cloture" style={{ marginTop: 8 }}>
                    <button type="button" className="btn btn-outline btn-sm" disabled={!modifiable || enCours}
                            title={modifiable ? 'Information tuteur : le message porte le lien personnel de la fiche de visite' : 'Lecture seule : votre profil ne permet pas de modifier ce module.'}
                            onClick={() => agir(() => stageAPI.preparerEnvoiTuteurs(departementId, anneeId, { genre: 'tuteur', tuteurId: t.id, cibles: [{ type: 'stagiaire', dossier_id: dossier.id }] }),
                                                (d) => onMessage && onMessage(d))}>
                        <i className="fa-solid fa-envelope" /> Écrire au tuteur
                    </button>
                    <button type="button" className="btn btn-outline btn-sm" disabled={!modifiable || enCours}
                            title={modifiable ? 'Information du suivi : présente le tuteur au maître de stage' : 'Lecture seule : votre profil ne permet pas de modifier ce module.'}
                            onClick={() => agir(() => stageAPI.preparerEnvoiTuteurs(departementId, anneeId, { genre: 'maitre', cibles: [{ type: 'stagiaire', dossier_id: dossier.id }] }),
                                                (d) => onMessage && onMessage(d))}>
                        <i className="fa-solid fa-briefcase" /> Prévenir le maître de stage
                    </button>
                </div>
            )}
            {/* Lot 3.5a-3 : les courriels au tuteur et au maître, comme les autres sections. */}
            {(dossier.envois || []).some((e) => ['tuteur_affectation', 'maitre_suivi'].includes(e.type_mail)) && (
                <ListeEnvois envois={(dossier.envois || []).filter((e) => ['tuteur_affectation', 'maitre_suivi'].includes(e.type_mail))} />
            )}
            {/* Lot 3.5c-3 (E145) : le RÉSUMÉ de la fiche de visite ; le détail vit dans le volet de l'onglet Évaluation. */}
            {s.fiche ? (
                <div className="stg-visite-resume">
                    <div>
                        <span className="eval-note-badge eval-note-visite">{s.fiche.total} / {s.fiche.total_max}</span>
                        {' '}Visite du {dateFr(s.fiche.date_visite)} · {s.fiche.mode_libelle}
                        {s.fiche.duree_minutes ? ` · ${s.fiche.duree_minutes >= 60 ? `${Math.floor(s.fiche.duree_minutes / 60)}h${String(s.fiche.duree_minutes % 60).padStart(2, '0')}` : `${s.fiche.duree_minutes} min`}` : ''}
                    </div>
                    <div className="stg-visite-resume-meta">
                        Fiche enregistrée le {dateFr(s.fiche.date_soumission)} par {s.fiche.remplie_par}{s.fiche.porte === 'compte' ? ' (Mes visites)' : ''}
                        {s.precedentes?.length > 0 && <> · modifiée {s.precedentes.length === 1 ? 'une fois' : `${s.precedentes.length} fois`} (versions conservées)</>}
                        {' · '}modèle « {s.fiche.modele_nom} »
                    </div>
                    {onVoirEvaluation && (
                        <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 6 }} onClick={() => onVoirEvaluation(dossier.id)}>
                            <i className="fa-solid fa-star-half-stroke" /> Voir le détail dans « Évaluation stagiaire »
                        </button>
                    )}
                </div>
            ) : t && (
                <div className="stg-section-avenir" style={{ marginTop: 8 }}>
                    <i className="fa-solid fa-file-pen" /> Fiche de visite pas encore remplie par le tuteur
                    {s.modele ? <> — modèle « {s.modele.nom} »{s.modele.figee_le ? ' (figé pour la campagne)' : ''}</> : ''}.
                </div>
            )}
        </div>
    );
}

function StagePanneauDossier({ dossierId, departementId, anneeId, user, modifiable = true, ancre, onFermer, onMessage, onChange, version = 0, onVoirEvaluation = null }) {
    const [dossier, setDossier] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [largeur, setLargeur] = useState(() => lireMemo(cleMemo('largeur', user), LARGEUR_DEFAUT, LARGEUR_MIN, LARGEUR_MAX));
    const [largeurHist, setLargeurHist] = useState(() => lireMemo(cleMemo('hist', user), HIST_DEFAUT, HIST_MIN, HIST_MAX));

    const glisse = useRef(null);
    // Les largeurs courantes, lisibles depuis la fin du glissé sans refabriquer
    // les écouteurs à chaque pixel. ⚠️ Déclarées AVANT `demarrerGlisse` : elles
    // sont dans sa fermeture, et les lire plus haut évite de dépendre de l'ordre
    // d'évaluation pour comprendre le fichier.
    const largeurRef = useRef(largeur);
    const histRef = useRef(largeurHist);
    useEffect(() => { largeurRef.current = largeur; }, [largeur]);
    useEffect(() => { histRef.current = largeurHist; }, [largeurHist]);

    /**
     * 22/10 — LA COLONNE « FICHE DE LIAISON ». Ouverte, elle RESTE ouverte d'un dossier à l'autre (une assistante
     * relit une série de fiches) : l'état est retenu par compte, comme les largeurs. Décision de Bertrand.
     */
    const [ficheOuverte, setFicheOuverte] = useState(() => lireMemo(cleMemo('fiche', user), 0, 0, 1) === 1);
    const [largeurFiche, setLargeurFiche] = useState(() => lireMemo(cleMemo('largeurFiche', user), FICHE_DEFAUT, FICHE_MIN, FICHE_MAX));
    const ficheRef = useRef(largeurFiche);
    useEffect(() => { ficheRef.current = largeurFiche; }, [largeurFiche]);
    const [ecranEtroit, setEcranEtroit] = useState(() => window.innerWidth < ECRAN_LARGE);
    const [histDeplie, setHistDeplie] = useState(false);
    useEffect(() => {
        const mesurer = () => setEcranEtroit(window.innerWidth < ECRAN_LARGE);
        window.addEventListener('resize', mesurer);
        return () => window.removeEventListener('resize', mesurer);
    }, []);
    const basculerFiche = useCallback(() => {
        setFicheOuverte((o) => { ecrireMemo(cleMemo('fiche', user), o ? 0 : 1); return !o; });
        setHistDeplie(false);
    }, [user]);

    /**
     * ⚠️ `relire` EST UNE FONCTION, PAS UN EFFET — et c'est ce qui permet au
     * lot 3.3 d'exister.
     *
     * Une décision change le dossier PENDANT qu'il est affiché : valider, renvoyer
     * au maître, refuser, exclure. Sans relecture, le panneau garderait sous les
     * yeux un état que la base n'a plus — et le bloc de décision resterait
     * affiché sur un dossier déjà validé.
     *
     * ⚠️ Elle NE dépend PAS d'un rappel de parent. La leçon la plus chère du lot
     * 1.3 bis d'Étudiants : un rappel de parent dans les dépendances d'un
     * `useCallback` qui recharge efface la saisie en cours.
     */
    const relire = useCallback(async () => {
        try {
            const r = await stageAPI.getDossier(dossierId, departementId);
            setDossier(r.data.dossier);
            return r.data.dossier;
        } catch (e) {
            setErreur(e.response?.data?.error || 'Lecture du dossier impossible');
            return null;
        }
    }, [dossierId, departementId]);

    useEffect(() => {
        let annule = false;
        setDossier(null);
        setErreur(null);
        stageAPI.getDossier(dossierId, departementId)
            .then((r) => { if (!annule) setDossier(r.data.dossier); })
            .catch((e) => {
                if (!annule) setErreur(e.response?.data?.error || 'Lecture du dossier impossible');
            });
        return () => { annule = true; };
    }, [dossierId, departementId]);

    /**
     * Lot 3.S-b2 : après un envoi par le SERVEUR (fenêtre d'envoi), le parent incrémente
     * `version` — le panneau se relit : statut avancé, message au journal, rappel SM10 levé.
     */
    const versionVue = useRef(version);
    useEffect(() => {
        if (versionVue.current === version) return;
        versionVue.current = version;
        relire();
    }, [version, relire]);

    // ── LES ACTES DE L'ÉTAPE ⑤ (lot 3.3) ───────────────────────────────────
    const [enCoursActe, setEnCoursActe] = useState(false);

    /**
     * ⚠️ LE PANNEAU SE RELIT SEUL, COMME LE TABLEAU (demandé le 2026-09-26).
     *
     * Bertrand : « la progression ne se met pas à jour ». Le maître de stage
     * répond dans SA fenêtre ; le panneau resté ouvert gardait l'état d'avant —
     * grille « envoyée » sur un dossier déjà validé. Même règle que le tableau :
     * au retour sur l'onglet, et chaque minute tant qu'il est visible.
     *
     * ⚠️ SILENCIEUSE et jamais pendant un acte : une panne réseau passagère ne doit
     * pas afficher d'erreur, et une relecture au milieu d'un envoi ferait mentir
     * l'écran. Les saisies en cours (motif de rejet, de renvoi) vivent dans des
     * états à part : la relecture ne les touche pas.
     */
    const enCoursRef = useRef(false);
    useEffect(() => { enCoursRef.current = enCoursActe; }, [enCoursActe]);
    useEffect(() => {
        let derniere = Date.now();
        const relireSeul = () => {
            if (document.visibilityState !== 'visible' || enCoursRef.current) return;
            const t = Date.now();
            if (t - derniere < 3000) return;
            derniere = t;
            stageAPI.getDossier(dossierId, departementId)
                .then((r) => { if (!enCoursRef.current) setDossier(r.data.dossier); })
                .catch(() => { /* silencieuse : la prochaine relecture réessaiera */ });
        };
        document.addEventListener('visibilitychange', relireSeul);
        window.addEventListener('focus', relireSeul);
        const minuterie = setInterval(relireSeul, 60000);
        return () => {
            document.removeEventListener('visibilitychange', relireSeul);
            window.removeEventListener('focus', relireSeul);
            clearInterval(minuterie);
        };
    }, [dossierId, departementId]);
    const [exclusionOuverte, setExclusionOuverte] = useState(false);
    // Lot 3.6 : « Renvoyer la grille » demande confirmation — il fait revenir un
    // dossier validé en attente. `null` = fermée, sinon le commentaire en cours.
    const [renvoiGrille, setRenvoiGrille] = useState(null);

    /**
     * Quelles listes d'envois sont déroulées.
     *
     * ⚠️ REMIS À ZÉRO À CHAQUE CHANGEMENT DE DOSSIER (écart E52). C'est ce que
     * faisait la `key` du bloc repliable ; maintenant que l'état vit ici, c'est
     * explicite. Sans ça, on ouvrirait un dossier pour le trouver déjà déroulé
     * sans l'avoir demandé.
     */
    const [envoisOuverts, setEnvoisOuverts] = useState({});
    /**
     * ⚠️ LE PANNEAU S'OUVRE **SUR** LA SECTION DEMANDÉE (test S20).
     *
     * Douze sections se suivent dans cette colonne. Arriver en haut quand on est
     * venu décider, c'est chercher — et chercher, c'est le moment où l'on se
     * trompe de dossier.
     *
     * ⚠️ Le défilement attend que le dossier soit LÀ : lancé au montage, il ne
     * trouverait rien à viser. Et il est « instantané » et non animé — une
     * animation qui démarre pendant que l'écran se remplit finit ailleurs
     * qu'où elle visait.
     */
    const refDecision = useRef(null);
    // ⚠️ UNE FOIS par dossier ouvert : le panneau se relit maintenant tout seul
    // (2026-09-26), et chaque relecture l'aurait ramené de force sur la décision.
    const defileFait = useRef(null);
    useEffect(() => {
        if (!dossier || ancre !== 'decision' || !refDecision.current) return;
        if (defileFait.current === dossierId) return;
        defileFait.current = dossierId;
        refDecision.current.scrollIntoView({ block: 'center' });
    }, [dossier, ancre, dossierId]);
    useEffect(() => { setEnvoisOuverts({}); }, [dossierId]);
    const basculerEnvois = useCallback((type) => {
        setEnvoisOuverts((o) => ({ ...o, [type]: !o[type] }));
    }, []);

    const envoisEtudiant = (dossier?.envois || []).filter((e) => e.type_mail === 'etudiant_lien');
    const envoisMaitre = (dossier?.envois || []).filter((e) => e.type_mail === 'maitre_fiche');
    const envoisGrille = (dossier?.envois || []).filter((e) => e.type_mail === 'grille_eval');

    /**
     * Un acte, son erreur, sa relecture — pour les cinq.
     *
     * ⚠️ UN SEUL CHEMIN, et la raison est celle du lot 3.2 bis : cinq copies
     * auraient divergé sur ce qui compte le plus ici — la relecture après coup,
     * et la remontée du message du serveur. Celui qui dit « ce dossier n'attend
     * pas de décision, rouvrez-le » vaut mieux que « Erreur serveur », mais
     * seulement s'il arrive jusqu'à l'écran.
     */
    /**
     * ⚠️ LE RAPPEL DU PARENT PASSE PAR UNE RÉFÉRENCE, JAMAIS PAR LES DÉPENDANCES.
     *
     * C'est le défaut le plus grave du lot 1.3 bis d'Étudiants : un rappel de
     * parent dans les dépendances d'un `useCallback` se refabrique à chaque
     * rendu du parent, relance le chargement au milieu d'une frappe et **efface
     * la saisie en cours** — ici, le motif de refus qu'on est en train d'écrire.
     *
     * La référence garde le rappel à jour sans jamais changer d'identité.
     */
    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    const agir = useCallback(async (faire, apres) => {
        setEnCoursActe(true);
        setErreur(null);
        try {
            const r = await faire();
            await relire();
            // Le tableau derrière le panneau doit suivre : un dossier validé
            // change de badge, de barre et d'indicateur.
            if (onChangeRef.current) onChangeRef.current();
            if (apres) apres(r.data);
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error
                || "L'action n'a pas abouti");
        } finally {
            setEnCoursActe(false);
        }
    }, [relire]);

    // ── Les deux poignées ───────────────────────────────────────────────────
    // ⚠️ Les écouteurs vivent sur `window` le temps du glissé : posés sur la
    // poignée seule, un mouvement rapide sortirait du curseur et la barre
    // resterait collée à la souris.
    const demarrerGlisse = useCallback((quoi) => (e) => {
        e.preventDefault();
        glisse.current = {
            quoi,
            xDepart: e.clientX,
            largeurDepart: quoi === 'panneau' ? largeur : quoi === 'fiche' ? largeurFiche : largeurHist
        };

        const bouger = (ev) => {
            const g = glisse.current;
            if (!g) return;
            if (g.quoi === 'panneau') {
                // Le panneau est ancré à DROITE : tirer vers la gauche l'élargit.
                const v = Math.min(LARGEUR_MAX, Math.max(LARGEUR_MIN, g.largeurDepart - (ev.clientX - g.xDepart)));
                setLargeur(v);
            } else if (g.quoi === 'fiche') {
                // La poignée est à GAUCHE de la colonne : tirer vers la gauche l'élargit (et le volet avec).
                setLargeurFiche(Math.min(FICHE_MAX, Math.max(FICHE_MIN, g.largeurDepart - (ev.clientX - g.xDepart))));
            } else {
                const v = Math.min(HIST_MAX, Math.max(HIST_MIN, g.largeurDepart - (ev.clientX - g.xDepart)));
                setLargeurHist(v);
            }
        };
        const relacher = () => {
            const g = glisse.current;
            glisse.current = null;
            window.removeEventListener('mousemove', bouger);
            window.removeEventListener('mouseup', relacher);
            document.body.style.userSelect = '';
            if (g) {
                // On mémorise À LA FIN du glissé, pas à chaque pixel : écrire cent
                // fois dans le stockage local pour un seul geste est inutile.
                if (g.quoi === 'panneau') ecrireMemo(cleMemo('largeur', user), largeurRef.current);
                else if (g.quoi === 'fiche') ecrireMemo(cleMemo('largeurFiche', user), ficheRef.current);
                else ecrireMemo(cleMemo('hist', user), histRef.current);
            }
        };
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', bouger);
        window.addEventListener('mouseup', relacher);
    }, [largeur, largeurHist, largeurFiche, user]);

    // La colonne ne s'affiche que s'il y a une fiche à lire ; le volet s'élargit de sa largeur (+ sa poignée).
    const montrerFiche = ficheOuverte && !!dossier?.fiche;
    const histReplie = montrerFiche && ecranEtroit && !histDeplie;
    const largeurVolet = montrerFiche ? Math.min(window.innerWidth, largeur + largeurFiche + 5) : largeur;
    const boutonFiche = (
        <button type="button" className={'fl-bascule' + (montrerFiche ? ' on' : '')} title="Voir la fiche de liaison complète"
                aria-pressed={montrerFiche} onClick={basculerFiche}>
            <i className="fa-solid fa-file-lines" /> {montrerFiche ? 'Masquer la fiche' : 'Voir la fiche'}
        </button>
    );

    // Échap referme — un panneau qui ne se referme qu'au clic sur une croix de
    // 20 pixels est un panneau qu'on subit.
    useEffect(() => {
        const auClavier = (e) => { if (e.key === 'Escape') onFermer(); };
        window.addEventListener('keydown', auClavier);
        return () => window.removeEventListener('keydown', auClavier);
    }, [onFermer]);

    return (
        <DeptEnvois.Provider value={departementId}>
        <div className="stg-panneau-fond" onClick={onFermer}>
            <div
                className={'stg-panneau' + (montrerFiche ? ' avec-fiche' : '')}
                style={{ width: largeurVolet }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Dossier de stage"
            >
                {/* Poignée du panneau entier */}
                <div
                    className="stg-poignee"
                    onMouseDown={demarrerGlisse('panneau')}
                    title="Glisser pour redimensionner le panneau"
                />

                <div className="stg-panneau-corps">
                    {erreur && (
                        <div className="stg-encart stg-encart-erreur">
                            <i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span>
                        </div>
                    )}

                    {!dossier && !erreur && (
                        <div className="stg-panneau-attente">
                            <i className="fa-solid fa-circle-notch stg-tourne" /> Lecture du dossier…
                        </div>
                    )}

                    {dossier && (
                        <>
                            <div className="stg-panneau-entete">
                                {/* ⚠️ Même composant et mêmes mesures que le tableau et que le
                                    module Étudiants : un visage ne change pas de forme selon
                                    l'écran qui le montre. */}
                                <PhotoEtudiant
                                    chemin={dossier.etudiant.photo_url}
                                    className="stg-avatar stg-avatar-photo stg-avatar-grand"
                                    alt=""
                                    secours={(
                                        <div className="stg-avatar stg-avatar-grand"
                                             style={{ background: teinteAvatar(dossier.etudiant.nom, dossier.etudiant.prenom) }}>
                                            {initiales(dossier.etudiant.nom, dossier.etudiant.prenom)}
                                        </div>
                                    )}
                                />
                                <div>
                                    <h3>{dossier.etudiant.nom} {dossier.etudiant.prenom}</h3>
                                    <div className="stg-panneau-sous">
                                        {dossier.formation_nom}{dossier.semestre ? ` · ${dossier.semestre}` : ''}
                                        {dossier.date_debut && ` · ${dateFr(dossier.date_debut)} → ${dateFr(dossier.date_fin)}`}
                                    </div>
                                </div>
                                <button type="button" className="action-btn" onClick={onFermer} title="Fermer (Échap)">
                                    <i className="fa-solid fa-xmark" />
                                </button>
                            </div>

                            <div className="detail-section">
                                <TitreSection icone="fa-bars-progress">Progression</TitreSection>
                                <StageBarreProgression
                                    segments={dossier.progression}
                                    faites={dossier.progression_faites}
                                    total={dossier.progression_total}
                                />
                                <div className="stg-panneau-action">
                                    <i className="fa-solid fa-arrow-right" /> {dossier.action_suivante?.libelle}
                                </div>
                                {/* Lot 3.S-b2 (SM10) : la décision est prise, son message n'est pas parti. */}
                                <RappelMessage dossier={dossier} modifiable={modifiable} enCours={enCoursActe}
                                               agir={agir} onMessage={onMessage} departementId={departementId} />
                            </div>

                            <div className="detail-section">
                                <TitreSection icone="fa-user-graduate">Étudiant</TitreSection>
                                <div className="detail-fields-2col">
                                    <Champ label="Numéro étudiant">{dossier.etudiant.code_etudiant}</Champ>
                                    <Champ label="Formation">{dossier.formation_nom}</Champ>
                                    <Champ label="Adresse">
                                        {dossier.etudiant.email_force && (
                                            <span className="stg-envoi-etat prep" style={{ marginLeft: 0, marginRight: 6 }}
                                                  title="Corrigée dans la fenêtre d'envoi « pour les prochains envois » — l'adresse du module Étudiants n'a pas changé">
                                                retenue pour ce dossier
                                            </span>
                                        )}
                                        {dossier.etudiant.email || (
                                            <span style={{ color: '#b45309' }}>
                                                aucune — à compléter dans le module Étudiants
                                            </span>
                                        )}
                                    </Champ>
                                    <Champ label="Source de l'adresse">
                                        {/* ⚠️ Dit d'OÙ vient l'adresse : la spec envoie à
                                            l'adresse institutionnelle, et se rabattre en
                                            silence sur une adresse personnelle serait une
                                            surprise le jour de l'envoi. */}
                                        {dossier.etudiant.email_force ? 'Corrigée dans la fenêtre d’envoi (prochains envois de ce dossier)'
                                            : dossier.etudiant.email_source === 'institutionnel' ? 'Institutionnelle'
                                            : dossier.etudiant.email_source === 'personnel'
                                                ? <span style={{ color: '#b45309' }}>Personnelle (institutionnelle absente)</span>
                                                : '—'}
                                    </Champ>
                                </div>
                            </div>

                            <div className="detail-section">
                                {/* ⚠️ « Modifier » est à DROITE du titre, comme les
                                    jalons des autres sections : c'est la place que
                                    l'œil connaît déjà dans ce panneau (E51). */}
                                <TitreSection icone="fa-list-check"
                                    droite={(dossier.statut !== 'valide' && modifiable) ? (
                                        <button type="button" className="stg-jalon stg-jalon-action"
                                                disabled={enCoursActe}
                                                onClick={() => setExclusionOuverte(true)}
                                                title="Exclure du suivi actif, ou réintégrer un dossier exclu">
                                            <i className="fa-solid fa-user-gear" /> Modifier
                                        </button>
                                    ) : null}>
                                    Suivi
                                </TitreSection>
                                <div className="detail-fields-2col">
                                    <Champ label="Statut">{dossier.action_suivante?.libelle}</Champ>
                                    {/* ⚠️ Un dossier exclu DIT POURQUOI. Sans le motif,
                                        « exclu » se lit comme une panne — et le
                                        commentaire est souvent la seule explication
                                        que quelqu'un ait pris la peine d'écrire. */}
                                    {dossier.motif_exclusion && (
                                        <Champ label="Motif d'exclusion">
                                            {LIBELLE_MOTIF[dossier.motif_exclusion] || dossier.motif_exclusion}
                                        </Champ>
                                    )}
                                    {dossier.commentaire_exclusion && (
                                        <div className="detail-field-inline" style={{ gridColumn: '1 / -1' }}>
                                            <label>Note</label>
                                            <span>{dossier.commentaire_exclusion}</span>
                                        </div>
                                    )}
                                    <Champ label="Tuteur pédagogique">
                                        {/* ⚠️ E98 : sans visite, il n'y a pas de tuteur à
                                            attendre — « Non affecté » se lisait comme un oubli. */}
                                        {dossier.tuteur
                                            ? `${dossier.tuteur.nom} ${dossier.tuteur.prenom || ''}`.trim()
                                            : dossier.visite_active === false
                                                ? <span style={{ color: '#94a3b8' }}>Sans objet (pas de visite)</span>
                                                : 'Non affecté'}
                                    </Champ>
                                    <Champ label="Maître de stage">{dossier.email_maitre_stage}</Champ>
                                    <Champ label="Période">
                                        {dossier.date_debut ? `${dateFr(dossier.date_debut)} → ${dateFr(dossier.date_fin)}` : '—'}
                                    </Champ>
                                </div>
                            </div>

                            {/* ── ① LE LIEN DE L'ÉTUDIANT ─────────────────────────── */}
                            <div className="detail-section">
                                {/* ⚠️ LE RÉSUMÉ DES ENVOIS EST SUR LA LIGNE DU TITRE.
                                    Demandé par Bertrand le 2026-09-25 : le bandeau
                                    de résumé coûtait une ligne pleine par section,
                                    pour deux mots d'information. Il passe à droite
                                    du titre, en petits caractères — et le jalon
                                    « lien ouvert » reste à sa place, au bout. */}
                                <TitreSection icone="fa-link"
                                    droite={<>
                                        {envoisEtudiant.length > 0 && (
                                            <ResumePliable
                                                ouvert={!!envoisOuverts.etudiant_lien}
                                                onBasculer={() => basculerEnvois('etudiant_lien')}
                                                resume={<ResumeEnvois envois={envoisEtudiant} />}
                                                titre="Voir le détail des messages"
                                            />
                                        )}
                                        {dossier.jeton_etudiant?.ouvert_le
                                            ? <span className="stg-jalon stg-jalon-ok">lien ouvert</span>
                                            : envoisEtudiant.length
                                                ? <span className="stg-jalon">préparé</span>
                                                : null}
                                    </>}>
                                    ① Lien étudiant
                                </TitreSection>
                                {/**
                                  * ⚠️ REPLIÉ PAR DÉFAUT, et l'état est remis à zéro à chaque
                                  * changement de dossier : on n'ouvre pas un dossier pour le
                                  * trouver déjà déroulé sans l'avoir demandé.
                                  * Défaut d'usage trouvé par Bertrand le 2026-09-17 : six
                                  * envois poussaient la fiche et la convention hors écran.
                                  */}
                                {envoisOuverts.etudiant_lien && <ListeEnvois envois={envoisEtudiant} />}
                                {!envoisEtudiant.length && <ListeEnvois envois={[]} />}
                                {dossier.jeton_etudiant && (
                                    <div className="detail-fields-2col" style={{ marginTop: 8 }}>
                                        {/* ⚠️ Les DATES du lien, jamais le lien : le panneau dit ce
                                            qui s'est passé, il ne permet pas de se faire passer
                                            pour l'étudiant. */}
                                        <Champ label="Lien ouvert le">
                                            {momentFr(dossier.jeton_etudiant.ouvert_le)
                                                || <span style={{ color: '#b45309' }}>jamais ouvert</span>}
                                        </Champ>
                                        <Champ label="Valable jusqu'au">{dateFr(dossier.jeton_etudiant.expire_le)}</Champ>
                                    </div>
                                )}
                            </div>

                            {/* ── ② LA FICHE DE LIAISON ───────────────────────────── */}
                            <div className="detail-section">
                                {/* 22/10 (rév. 11) : « Voir la fiche » à droite de la date — ouvre la colonne. */}
                                <TitreSection icone="fa-user-pen" classe="fl-titre"
                                    droite={dossier.fiche?.soumise_etudiant_le
                                        ? <span className="fl-droite">
                                            <span className="stg-jalon stg-jalon-ok">
                                              saisie le {dateFr(dossier.fiche.soumise_etudiant_le)}
                                            </span>
                                            {boutonFiche}
                                          </span>
                                        : null}>
                                    ② Fiche étudiant
                                </TitreSection>
                                {dossier.fiche ? (
                                    <div className="detail-fields-2col">
                                        <Champ label="Entreprise">{dossier.fiche.entreprise_nom}</Champ>
                                        <Champ label="Ville">
                                            {[dossier.fiche.entreprise_cp, dossier.fiche.entreprise_ville]
                                                .filter(Boolean).join(' ') || null}
                                        </Champ>
                                        <Champ label="Maître de stage">
                                            <MaitreEtCivilite fiche={dossier.fiche} modifiable={modifiable} enCours={enCoursActe}
                                                              onEnregistrer={(c, fin) => agir(() => stageAPI.saisirCiviliteMaitre(dossier.id, departementId, c), fin)} />
                                        </Champ>
                                        <Champ label="Fonction">{dossier.fiche.maitre_fonction}</Champ>
                                        <Champ label="Courriel du maître">{dossier.fiche.maitre_email}</Champ>
                                        <Champ label="Téléphone">{dossier.fiche.maitre_telephone}</Champ>
                                        {dossier.fiche.missions && (
                                            <div className="detail-field-inline" style={{ gridColumn: '1 / -1' }}>
                                                <label>Missions</label>
                                                <span style={{ whiteSpace: 'pre-wrap' }}>{dossier.fiche.missions}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="stg-section-avenir">
                                        <i className="fa-solid fa-hourglass-half" /> L'étudiant n'a pas encore
                                        soumis sa fiche.
                                    </div>
                                )}
                            </div>

                            {/* ── ③ LA VALIDATION DU RESPONSABLE (E173, v85) ─────────
                                La fiche de l'étudiant est relue par le responsable des stages AVANT de partir au
                                maître. Le bloc de décision n'apparaît que sur une fiche qui l'attend vraiment ; sinon
                                la section dit ce qui a été décidé (l'historique), ou « sans objet ». */}
                            {(dossier.statut === 'fiche_etudiant_saisie' && dossier.validation_responsable_active && modifiable) ? (
                                <div ref={refDecision}>
                                <StageBlocDecision
                                    etape="responsable"
                                    dossier={dossier}
                                    enCours={enCoursActe}
                                    onValider={() => agir(() => stageAPI.validerFicheEtudiant(dossier.id, departementId))}
                                    onRenvoyer={(commentaire) => agir(
                                        () => stageAPI.renvoyerAEtudiant(dossier.id, departementId, commentaire),
                                        (d) => onMessage && onMessage(d.message))}
                                    onRefuser={(motif) => agir(
                                        () => stageAPI.refuserStage(dossier.id, departementId, motif),
                                        (d) => onMessage && onMessage(d.message))}
                                />
                                </div>
                            ) : (
                                <div className="detail-section">
                                    {(() => {
                                        const acte = (dossier.historique || []).find((h) =>
                                            ['fiche_etudiant_validee', 'fiche_etudiant_a_corriger'].includes(h.action));
                                        return (
                                            <>
                                                <TitreSection icone="fa-user-check"
                                                    droite={!dossier.validation_responsable_active
                                                        ? <span className="stg-jalon">sans objet</span>
                                                        : acte?.action === 'fiche_etudiant_validee'
                                                            ? <span className="stg-jalon stg-jalon-ok">validée</span>
                                                            : acte ? <span className="stg-jalon">renvoyée à l'étudiant</span> : null}>
                                                    ③ Validation du responsable
                                                </TitreSection>
                                                <RappelMessage dossier={dossier} modifiable={modifiable} enCours={enCoursActe} agir={agir}
                                                               onMessage={onMessage} departementId={departementId}
                                                               types={['fiche_etudiant_a_corriger']} />
                                                {!dossier.validation_responsable_active ? (
                                                    <div className="stg-section-avenir">
                                                        <i className="fa-solid fa-circle-minus" /> Sans objet : ce processus envoie la
                                                        fiche directement au maître (étape ③ désactivée dans « Le processus »).
                                                    </div>
                                                ) : acte ? (
                                                    <>
                                                        <div className="detail-fields-2col">
                                                            <Champ label="Décision">{acte.libelle}</Champ>
                                                            <Champ label="Par">{acte.acteur}</Champ>
                                                        </div>
                                                        {acte.commentaire && (
                                                            <div className="hist-commentaire" style={{ marginTop: 6 }}>« {acte.commentaire} »</div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="stg-section-avenir">
                                                        <i className="fa-solid fa-hourglass-half" /> {dossier.statut === 'fiche_etudiant_saisie'
                                                            ? 'La fiche attend la validation du responsable des stages.'
                                                            : "Le responsable relira la fiche une fois que l'étudiant l'aura soumise."}
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* ── ④ LA FICHE PART AU MAÎTRE DE STAGE ──────────────── */}
                            <div className="detail-section">
                                <TitreSection icone="fa-paper-plane"
                                    droite={<>
                                        {envoisMaitre.length > 0 && (
                                            <ResumePliable
                                                ouvert={!!envoisOuverts.maitre_fiche}
                                                onBasculer={() => basculerEnvois('maitre_fiche')}
                                                resume={<ResumeEnvois envois={envoisMaitre} destinataire="au maître de stage" />}
                                                titre="Voir le détail des messages"
                                            />
                                        )}
                                        {dossier.jeton_maitre?.ouvert_le
                                            ? <span className="stg-jalon stg-jalon-ok">lien ouvert</span>
                                            : envoisMaitre.length
                                                ? <span className="stg-jalon">préparé</span>
                                                : null}
                                    </>}>
                                    ④ Fiche envoyée au maître
                                </TitreSection>
                                {/* ⚠️ Même pli que la section ① (écart E52) : un dossier
                                    relancé plusieurs fois pousserait tout le reste hors
                                    de l'écran. */}
                                {envoisOuverts.maitre_fiche && <ListeEnvois envois={envoisMaitre} />}
                                {!envoisMaitre.length && (
                                    <div className="stg-section-avenir">
                                        <i className="fa-solid fa-hourglass-half" /> La fiche n'a pas encore
                                        été préparée pour le maître de stage.
                                    </div>
                                )}
                                <div className="detail-fields-2col" style={{ marginTop: 8 }}>
                                    <Champ label="Destinataire">
                                        {dossier.fiche?.maitre_email || dossier.email_maitre_stage}
                                    </Champ>
                                    {/* ⚠️ Les DATES du lien, jamais le lien (écart E50). La
                                        maquette affiche ici « tok_d1e2f3… » ; faire descendre
                                        la chaîne du jeton jusqu'à cet écran permettrait à
                                        quiconque le voit de remplir la fiche À LA PLACE du
                                        maître de stage — et l'historique porterait son nom. */}
                                    <Champ label="Lien ouvert le">
                                        {dossier.jeton_maitre
                                            ? (momentFr(dossier.jeton_maitre.ouvert_le)
                                                || <span style={{ color: '#b45309' }}>jamais ouvert</span>)
                                            : null}
                                    </Champ>
                                </div>
                            </div>

                            {/* ── ⑤ CE QUE LE MAÎTRE A RÉPONDU ────────────────────── */}
                            <div className="detail-section">
                                <TitreSection icone="fa-building" classe="fl-titre"
                                    droite={dossier.fiche?.soumise_maitre_le
                                        ? <span className="fl-droite">
                                            <span className="stg-jalon stg-jalon-ok">
                                              validée le {dateFr(dossier.fiche.soumise_maitre_le)}
                                            </span>
                                            {boutonFiche}
                                          </span>
                                        : null}>
                                    ⑤ Fiche maître validée
                                </TitreSection>
                                {dossier.fiche?.soumise_maitre_le ? (
                                    <>
                                        {/* ⚠️ `detail-fields-2col` + `detail-field-inline` : c'est le
                                            SEUL couple de classes que porte notre feuille de style.
                                            La maquette écrit `detail-field` — qui n'existe que chez
                                            elle. Repris tel quel, ce bloc se serait affiché sans
                                            aucun style, et rien ne l'aurait signalé avant l'image. */}
                                        <div className="detail-fields-2col">
                                            <div className="detail-field-inline" style={{ gridColumn: '1 / -1' }}>
                                                <label>Convention université</label>
                                                {/* ⚠️ L'EMOJI 🏛️, comme la maquette et comme l'en-tête
                                                    de colonne — pas `fa-landmark` (écart E46 : emoji
                                                    dans les textes, Font Awesome pour les boutons). */}
                                                <span>
                                                    {dossier.fiche.convention_universite
                                                        ? <>🏛️ <b style={{ color: '#16a34a' }}>Oui</b>
                                                            {' '}— convention de l'université retenue</>
                                                        : <>L'entreprise impose sa propre convention</>}
                                                </span>
                                            </div>
                                        </div>
                                        {/* E173 (v85) : CE QUE LE MAÎTRE A MODIFIÉ dans la saisie de l'étudiant (la v85 garde
                                            cette saisie) — « avant → après ». Les responsables des stages ont été prévenus
                                            (courriel + cloche) ; l'historique dit qui. Rien de modifié : une ligne le dit. */}
                                        {(dossier.fiche.modifications_maitre || []).length > 0 ? (
                                            <div className="stg-modifs-maitre">
                                                <div className="stg-modifs-titre">
                                                    <i className="fa-solid fa-pen" /> Le maître a modifié {dossier.fiche.modifications_maitre.length} information{dossier.fiche.modifications_maitre.length > 1 ? 's' : ''} saisie{dossier.fiche.modifications_maitre.length > 1 ? 's' : ''} par l'étudiant
                                                </div>
                                                <table>
                                                    <thead><tr><th>Champ</th><th>Saisi par l'étudiant</th><th>Après le maître</th></tr></thead>
                                                    <tbody>
                                                        {dossier.fiche.modifications_maitre.map((m) => (
                                                            <tr key={m.champ}>
                                                                <td><b>{m.libelle}</b></td>
                                                                <td className="avant">{m.avant}</td>
                                                                <td>{m.apres || <i>(vide)</i>}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                {(() => {
                                                    const alerte = (dossier.historique || []).find((h) => h.action === 'fiche_modifiee_maitre');
                                                    return alerte && (
                                                        <div className="stg-modifs-pied">
                                                            <i className="fa-solid fa-bell" /> {momentFr(alerte.date) || dateFr(alerte.date)} — {(alerte.commentaire || '').split(' — ').slice(1).join(' — ') || 'responsables prévenus'}.
                                                            {' '}<span>Pour information : la décision ⑥ reste à l'assistante.</span>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ) : (
                                            <div className="stg-section-avenir" style={{ marginTop: 6 }}>
                                                <i className="fa-solid fa-circle-info" /> Le maître n'a rien changé à ce que l'étudiant avait
                                                saisi{dossier.fiche.modifications_maitre ? '' : ' (ou la fiche date d\'avant la v85 : comparaison impossible)'}.
                                                La section ② montre la fiche telle qu'il l'a validée.
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="stg-section-avenir">
                                        <i className="fa-solid fa-hourglass-half" /> Le maître de stage
                                        n'a pas encore validé la fiche.
                                    </div>
                                )}
                            </div>
                            {/* ── ⑥ LA VALIDATION ADMINISTRATIVE (lot 3.3 ; E173 : ex « Validation DPT ») ───
                                ⚠️ Le bloc n'apparaît QUE sur un dossier qui attend
                                vraiment une décision. Affiché en permanence, il
                                proposerait de valider une fiche déjà validée — et
                                le serveur refuserait, ce qui ferait cliquer dans
                                le vide. */}
                            {(dossier.statut === 'fiche_maitre_validee' && modifiable) ? (
                                <div ref={refDecision}>
                                <StageBlocDecision
                                    etape="administrative"
                                    dossier={dossier}
                                    enCours={enCoursActe}
                                    onValider={() => agir(
                                        () => stageAPI.validerFiche(dossier.id, departementId),
                                        (d) => onMessage && onMessage(d.message))}
                                    onRenvoyer={(commentaire) => agir(
                                        () => stageAPI.renvoyerAuMaitre(dossier.id, departementId, commentaire),
                                        (d) => onMessage && onMessage(d.message))}
                                    onRefuser={(motif) => agir(
                                        () => stageAPI.refuserStage(dossier.id, departementId, motif),
                                        // E137 : comme les CAS 1 et 2, le message suit la décision.
                                        (d) => onMessage && onMessage(d.message))}
                                />
                                </div>
                            ) : (
                                <div className="detail-section">
                                    <TitreSection icone="fa-circle-check"
                                        droite={dossier.historique?.some((h) => h.action === 'fiche_validee')
                                            ? <span className="stg-jalon stg-jalon-ok">validée</span>
                                            : null}>
                                        ⑥ Validation administrative
                                    </TitreSection>
                                    <RappelMessage dossier={dossier} modifiable={modifiable} enCours={enCoursActe} agir={agir}
                                                   onMessage={onMessage} departementId={departementId}
                                                   types={['fiche_validee', 'fiche_a_corriger']} />
                                    {(() => {
                                        // ⚠️ La décision se LIT dans l'historique, la seule
                                        // source qui garde son auteur et son motif — et qui
                                        // ne se réécrit pas.
                                        const acte = (dossier.historique || []).find((h) =>
                                            ['fiche_validee', 'fiche_a_corriger', 'stage_refuse'].includes(h.action));
                                        if (!acte) {
                                            return (
                                                <div className="stg-section-avenir">
                                                    <i className="fa-solid fa-hourglass-half" /> La validation administrative
                                                    viendra après celle du maître de stage.
                                                </div>
                                            );
                                        }
                                        return (
                                            <>
                                                <div className="detail-fields-2col">
                                                    <Champ label="Décision">{acte.libelle}</Champ>
                                                    <Champ label="Par">{acte.acteur}</Champ>
                                                </div>
                                                {acte.commentaire && (
                                                    <div className="hist-commentaire" style={{ marginTop: 6 }}>
                                                        « {acte.commentaire} »
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                            {/* L3b (E172, décision de Bertrand) : un APPRENTI n'a ni convention ni documents de retour. */}
                            {dossier.parcours === 'apprenti' ? (
                                <>
                                    {[['fa-file-contract', '⑦ Convention'], ['fa-folder-open', '⑧ Documents de retour']].map(([ic, t]) => (
                                        <div className="detail-section" key={t}>
                                            <TitreSection icone={ic} droite={<span className="stg-jalon">sans objet</span>}>{t}</TitreSection>
                                            <div className="stg-section-avenir">
                                                <i className="fa-solid fa-circle-minus" /> Sans objet : apprenti (contrat d'apprentissage) — le dossier passe de la
                                                validation administrative (⑥) à la visite (⑨).
                                            </div>
                                        </div>
                                    ))}
                                </>
                            ) : (<>
                            <SectionConvention dossier={dossier} departementId={departementId}
                                modifiable={modifiable} enCours={enCoursActe} agir={agir} onMessage={onMessage} />
                            <SectionDocuments dossier={dossier} departementId={departementId}
                                modifiable={modifiable} enCours={enCoursActe} agir={agir} onMessage={onMessage} />
                            </>)}
                            {/* ⚠️ E98 : une visite DÉSACTIVÉE dans la configuration ne
                                s'annonce pas « livrée au lot 3.5 » — elle n'aura jamais
                                lieu. Même lecture que la maquette (pastille grise « N/A »). */}
                            {dossier.sections?.visite?.sans_objet ? (
                                <div className="detail-section">
                                    <TitreSection icone="fa-car"
                                        droite={<span className="stg-jalon">sans objet</span>}>
                                        ⑨ Visite du tuteur
                                    </TitreSection>
                                    <div className="stg-section-avenir">
                                        <i className="fa-solid fa-circle-minus" /> Sans objet : pas de visite pour
                                        cette formation (étape ⑨ désactivée dans Paramétrage → Processus).
                                    </div>
                                </div>
                            ) : (
                                <SectionTuteur dossier={dossier} departementId={departementId} anneeId={anneeId}
                                    modifiable={modifiable} enCours={enCoursActe} agir={agir} onMessage={onMessage}
                                    onVoirEvaluation={onVoirEvaluation} />
                            )}
                            <SectionEvaluation dossier={dossier} modifiable={modifiable} enCours={enCoursActe}
                                envois={envoisGrille} envoisOuverts={!!envoisOuverts.grille_eval}
                                basculer={() => basculerEnvois('grille_eval')}
                                // ⚠️ `relance` vient de l'écran, le destinataire du serveur (E61).
                                onEnvoyer={(relance) => agir(
                                    () => stageAPI.preparerEnvoiGrille(dossier.id, departementId),
                                    (d) => onMessage && onMessage({ ...d, relance }))}
                                onRenvoyer={() => setRenvoiGrille('')} />
                            {/* Lot D.1 — la conservation des documents du dossier (maquette : après les étapes). */}
                            <BlocConservation departementId={departementId} objetTable="suivi_stage" objetId={dossier.id} onChange={relire} />
                        </>
                    )}
                </div>

                {/* 22/10 (maquette rév. 11) : la fiche de liaison ENTIÈRE, entre le dossier et l'Historique. */}
                {montrerFiche && (
                    <>
                        <div
                            className="stg-poignee stg-poignee-fiche"
                            onMouseDown={demarrerGlisse('fiche')}
                            title="Glisser pour ajuster la largeur de la fiche de liaison"
                        />
                        <div className="stg-panneau-fiche" style={{ width: largeurFiche }} aria-label="Fiche de liaison">
                            <div className="fl-tete">
                                <div className="fl-tete-l1">
                                    <i className="fa-solid fa-file-lines" /> Fiche de liaison
                                    <button type="button" title="Fermer la fiche" aria-label="Fermer la fiche" onClick={basculerFiche}>
                                        <i className="fa-solid fa-xmark" />
                                    </button>
                                </div>
                                <LigneEtatFiche fiche={dossier.fiche} />
                            </div>
                            <div className="fl-corps">
                                <StageFicheLiaisonContenu fiche={dossier.fiche} />
                            </div>
                        </div>
                    </>
                )}

                {/* Poignée propre à la colonne Historique */}
                <div
                    className="stg-poignee stg-poignee-hist"
                    onMouseDown={demarrerGlisse('hist')}
                    title="Glisser pour ajuster la largeur de l'historique"
                />

                {/* Écran étroit et fiche ouverte : l'Historique se réduit à une bande ; un clic le déplie. */}
                <div className={'stg-panneau-hist' + (histReplie ? ' replie' : '')} style={{ width: largeurHist }}
                     onClick={histReplie ? () => setHistDeplie(true) : undefined}
                     title={histReplie ? "Déplier l'historique" : undefined}>
                    <div className="detail-hist-title">
                        <i className="fa-solid fa-clock-rotate-left" /> Historique
                    </div>
                    <div className="detail-hist-body">
                        {dossier && dossier.historique.length === 0 && (
                            <div className="stg-section-avenir" style={{ margin: 0 }}>
                                Aucun acte enregistré pour l'instant. Chaque envoi, validation
                                ou exclusion y laissera une entrée horodatée.
                            </div>
                        )}
                        {dossier && dossier.historique.map((h, i) => (
                            <div className="hist-entry" key={i}>
                                {/* ⚠️ DATE **ET HEURE**, comme la maquette : deux actes du
                                    même jour — « fiche envoyée au maître » puis « fiche
                                    validée » — se lisent dans le désordre sans l'heure. */}
                                <div className="hist-date">{momentFr(h.date) || dateFr(h.date)}</div>
                                {/* ⚠️ « — » pour un formulaire public, comme la spec (S27) et
                                    la maquette. Ce n'est pas une valeur manquante : c'est
                                    l'information exacte — personne de l'établissement n'a
                                    fait ça. L'infobulle le dit en toutes lettres, pour que
                                    le tiret ne reste pas une énigme. */}
                                <div className={'hist-actor' + (h.acteur ? '' : ' auto')}
                                     title={h.acteur
                                        ? h.acteur
                                        : "Acte d'un formulaire public — l'étudiant et le maître de stage n'ont pas de compte"}>
                                    {h.acteur || '—'}
                                </div>
                                <div className="hist-action">
                                    {h.libelle || h.action}
                                    {/* Lot 3.4 : QUEL mode, QUEL fichier — sans quoi « Convention
                                        remplacée » ne dit pas ce qui a remplacé quoi. */}
                                    {h.mode_libelle && <> — {h.mode_libelle}</>}
                                    {h.civilite && <> — {h.civilite}</>}
                                    {h.fichier && <> — <i>{h.fichier}</i></>}
                                    {h.signataire && <> — signée par {h.signataire}</>}
                                                {/* Lot 3.5a : le tuteur (et l'ancien), figés au moment de l'acte. */}
                                                {h.action === 'tuteur_remplace' && h.ancien_tuteur ? <> — {h.ancien_tuteur} → {h.tuteur}</>
                                                    : (h.tuteur || h.ancien_tuteur) && <> — {h.tuteur || h.ancien_tuteur}</>}
                                </div>
                                {/* ⚠️ LE COMMENTAIRE EST L'INFORMATION, pas un détail : sur un
                                    renvoi au maître ou un refus, c'est la seule trace du
                                    POURQUOI — et elle est immuable. Le taire reviendrait à
                                    garder la décision sans sa raison. */}
                                {h.commentaire && (
                                    <div className="hist-commentaire">« {h.commentaire} »</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ⚠️ La modale d'exclusion est rendue HORS du panneau, au niveau du
                fond : le panneau a son propre défilement et ses deux poignées de
                redimensionnement, et une modale posée dedans défilerait avec lui
                — donc sortirait de l'écran dès qu'on fait glisser la liste. */}
            {/* Lot 3.6 (E100) : renvoyer la grille — même règle que l'exclusion, la
                modale vit HORS du panneau pour ne pas défiler avec lui. */}
            {renvoiGrille !== null && dossier && (
                <StageModale
                    titre="Renvoyer la grille au maître de stage" icone="fa-rotate" enCours={enCoursActe}
                    libelleValider="Renvoyer avec un nouveau lien"
                    onFermer={() => setRenvoiGrille(null)}
                    onValider={() => {
                        const commentaire = renvoiGrille;
                        setRenvoiGrille(null);
                        agir(() => stageAPI.renvoyerGrille(dossier.id, departementId, commentaire),
                             (d) => onMessage && onMessage(d));
                    }}
                >
                    <div className="stg-encart stg-encart-alerte" style={{ marginBottom: 10 }}>
                        <i className="fa-solid fa-rotate" />
                        <span>
                            Un <b>nouveau lien</b> remplace le précédent, qui <b>ne fonctionnera plus</b>.
                            {dossier.sections?.evaluation?.resultat && <>
                                {' '}La grille reçue le {dateFr(dossier.sections.evaluation.resultat.date_soumission)} est
                                {' '}<b>gardée</b> dans le dossier, mais celui-ci repasse <b>en attente de la grille</b> :
                                il ne sera plus validé tant que le maître n'aura pas renvoyé la sienne.
                            </>}
                        </span>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label htmlFor="renvoi-grille-motif">Pourquoi ? <span style={{ color: '#94a3b8' }}>(facultatif — gardé dans l'historique)</span></label>
                        <textarea id="renvoi-grille-motif" className="input-sm" rows={2} value={renvoiGrille}
                                  onChange={(e) => setRenvoiGrille(e.target.value)}
                                  placeholder="Ex. : le maître a signalé une erreur de saisie" />
                    </div>
                </StageModale>
            )}

            {exclusionOuverte && dossier && (
                <StageModaleExclusion
                    dossier={dossier}
                    enCours={enCoursActe}
                    onFermer={() => setExclusionOuverte(false)}
                    onExclure={(motif, commentaire) => {
                        setExclusionOuverte(false);
                        agir(() => stageAPI.exclureDossier(dossier.id, departementId, motif, commentaire));
                    }}
                    onReintegrer={() => {
                        setExclusionOuverte(false);
                        agir(() => stageAPI.reintegrerDossier(dossier.id, departementId));
                    }}
                />
            )}
        </div>
        </DeptEnvois.Provider>
    );
}

export default StagePanneauDossier;
